import { ModalTemplateWrapper } from '#app/components/Modals/V2/GlobalModalSystem/ModalTemplateWrapper/ModalTemplateWrapper.js';
import { type CommonTemplateProps } from '#app/components/Modals/V2/GlobalModalSystem/types.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import imgSource from '../assets/preview-ended-hero.webp';
import { getRoute } from '@contra/routes';
import { useCallback } from 'react';

type SearchPreviewEndedModalProps = CommonTemplateProps & {
  readonly onSubmit: () => void;
};

export const SearchPreviewEndedModal = ({
  closeModal,
  isOpen,
  modalName,
  onSubmit,
}: SearchPreviewEndedModalProps): JSX.Element => {
  const navigation = useNavigation();
  const { track } = useAnalytics();
  const onPostJobForFree = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'post_a_job_for_free',
      section: modalName,
    });
    navigation.push(
      getRoute('/client/opportunities', {
        search: {
          modalName: 'JobCreationModal',
        },
      }),
    );
    onSubmit();
    closeModal();
  }, [closeModal, modalName, navigation, onSubmit, track]);

  const onLimitedSearch = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'limited_search_preview_ended',
      section: modalName,
    });
    onSubmit();
    closeModal();
  }, [closeModal, modalName, onSubmit, track]);

  const onModalClose = useCallback(() => {
    onSubmit();
    closeModal();
  }, [closeModal, onSubmit]);

  const hero = (
    <img
      alt="Hero image for limited search preview ended modal"
      src={imgSource}
      style={{
        height: 'auto',
        maxHeight: '490px',
        maxWidth: '1088px',
        width: '100%',
      }}
    />
  );

  return (
    <ModalTemplateWrapper
      ariaLabel="24-hour preview ended modal"
      ctaOptions={{
        primaryCTA: {
          ariaLabel: 'Browse limited search',
          handleOnClick: onLimitedSearch,
          label: 'Browse limited search',
        },
        secondaryCTA: {
          ariaLabel: 'Post a job for free',
          handleOnClick: onPostJobForFree,
          label: 'Post a job for free',
        },
      }}
      desktopModalSize="md"
      headerHero={hero}
      headerText="Your 24-hour preview has ended"
      isFullscreenOnMobile
      isOpen={isOpen}
      onClose={onModalClose}
      subheaderText="Unlock full search access with a membership. In the meantime, you can continue with limited search or post a job for free to connect with top talent."
    />
  );
};
